/**
 * `BoundRig` — the ONE object that drives a real three.js skeleton from canonical poses (D18).
 *
 * Both the editor viewport and the runtime SDK use it, so a pose looks identical in the tool and
 * in the game. It holds no editor state and imports only `three`, `src/shared` and `src/rig`.
 *
 * `applyPose` runs every frame during playback and therefore allocates nothing: all quaternion
 * work goes through preallocated scratch tuples.
 */

import { Matrix3, Matrix4, Quaternion, Vector3 } from 'three';
import type { Object3D } from 'three';

import { AppError, CANONICAL_BONE_IDS, cloneQuat, identityQuat, normalizeQuat } from '../shared';
import type { CanonicalBoneId, Pose, QuaternionTuple, RigBinding, Vec3Tuple } from '../shared';
import { analyzeSkeleton, collectBoneObjects, type SourceBoneInfo } from '../rig/sourceSkeleton';
import { canonicalToSourceLocal, sourceLocalToCanonical } from '../rig/retarget';
import {
  IDENTITY_ALIGNMENT,
  absoluteToRelative,
  relativeToAbsolute,
  resolveBoneAlignments,
  type BoneAlignment,
} from '../rig/restAlignment';

export interface BoundBone {
  id: CanonicalBoneId;
  object: Object3D;
  /** Local rotation of `object` in the rest pose. */
  restLocal: QuaternionTuple;
  /** Rest world orientation including the binding's world correction — `W_b` in plan D5. */
  W_b: QuaternionTuple;
  /**
   * Rest-pose alignment factors (`RigBinding.restAlignment`, rigVersion `humanoid-v2`). They
   * translate between the rig-independent clip value and the rotation this particular rig needs.
   * All-identity for a rig whose rest already is the canonical straight T-pose.
   */
  alignment: BoneAlignment;
}

/**
 * Structural contract consumed by the GLB exporter. `BoundRig` satisfies it, so the exporter
 * never has to depend on the class itself.
 */
export interface RigDriver {
  boneObject(id: CanonicalBoneId): Object3D | undefined;
  toSourceLocal(id: CanonicalBoneId, q: QuaternionTuple): QuaternionTuple;
  mappedBoneIds(): CanonicalBoneId[];
  /**
   * Hips local position for a `hipsY` value; `null` when the channel is disabled on this rig.
   *
   * OPTIONAL, matching the exporter's own structural copy of this interface: a hand-written
   * driver that predates the channel is still a valid `RigDriver`, and a consumer that gets no
   * position out of it writes no position track. `BoundRig` always implements it.
   */
  hipsLocalPosition?(hipsY: number): Vec3Tuple | null;
}

const worldQuatScratch = new Quaternion();

/**
 * Resolve a binding against a concrete model.
 *
 * Throws `AppError('BIND_FAILED')` listing the source bone names the model does not have, and
 * `AppError('BIND_NO_BONES_MAPPED')` when the binding names no canonical bone this model has.
 */
export function bindRig(root: Object3D, binding: RigBinding): BoundRig {
  const analysis = analyzeSkeleton(root);
  const objects = collectBoneObjects(root);

  const bones = new Map<CanonicalBoneId, BoundBone>();
  const restInfo = new Map<CanonicalBoneId, SourceBoneInfo>();
  const missing: { bone: CanonicalBoneId; sourceName: string }[] = [];

  // Resolved before the loop so a bone's parent factor can skip unmapped ancestors.
  const alignments = resolveBoneAlignments(
    binding.restAlignment,
    (id) => binding.canonicalToSourceBone[id] != null,
  );

  for (const id of CANONICAL_BONE_IDS) {
    const sourceName = binding.canonicalToSourceBone[id];
    if (!sourceName) continue;

    const object = objects.get(sourceName);
    const info = analysis.byName.get(sourceName);
    if (!object || !info) {
      missing.push({ bone: id, sourceName });
      continue;
    }

    bones.set(id, {
      id,
      object,
      restLocal: cloneQuat(info.restLocalQuaternion),
      W_b: cloneQuat(binding.restRotationOffsets[id] ?? identityQuat()),
      alignment: alignments.get(id) ?? IDENTITY_ALIGNMENT,
    });
    restInfo.set(id, info);
  }

  if (missing.length > 0) {
    const list = missing.map((m) => `${m.bone} → "${m.sourceName}"`).join(', ');
    throw new AppError(
      'BIND_FAILED',
      `This model has no bone for ${missing.length} mapped canonical bone(s): ${list}.`,
      { missing },
    );
  }

  // A rig that drives zero bones is not a rig — it is a silent failure with a happy face on
  // (audit §2.5 #5). `bindRig` used to return it, and then `autoBind`, `play` and `update` all
  // succeeded forever while the character stood perfectly still: `applyPose` iterated an empty
  // map, nothing threw, and the only evidence was in `validation` / `mappedBoneIds()`, which
  // nothing told the integrator to check. The heuristic path reaches here whenever auto-mapping
  // recognised none of the model's bone names, which is exactly the "wrong model wired up" case
  // that must be loud. `details.skeletonBoneNames` is what the model actually contained, so a
  // caller can show it, log it, or match it against its own naming scheme.
  if (bones.size === 0) {
    const boneNames = analysis.bones.map((b) => b.name);
    throw new AppError(
      'BIND_NO_BONES_MAPPED',
      'This binding maps none of the canonical bones onto this model, so nothing could animate ' +
        `it. The model's skeleton has ${boneNames.length} bone(s)` +
        (boneNames.length > 0 ? `, starting with: ${boneNames.slice(0, 8).join(', ')}.` : '.') +
        ' Check that this is the right model, or map its bones by hand.',
      {
        skeletonBoneCount: boneNames.length,
        skeletonBoneNames: boneNames,
        mappedBoneCount: 0,
      },
    );
  }

  return new BoundRig(root, binding, bones, restInfo);
}

export class BoundRig implements RigDriver {
  readonly root: Object3D;
  readonly binding: RigBinding;
  readonly bones: Map<CanonicalBoneId, BoundBone>;

  /** Bind-pose world positions of the mapped bones, in source world space. */
  private readonly restWorldPositions: Partial<Record<CanonicalBoneId, Vec3Tuple>>;

  /** Bind-pose world orientations of the mapped bones, in source world space. */
  private readonly restWorldQuaternions: Partial<Record<CanonicalBoneId, QuaternionTuple>>;

  /** Per-frame scratch. `applyPose` must not allocate (SPEC "Performance Expectations"). */
  private readonly scratchLocal: QuaternionTuple = [0, 0, 0, 1];
  /** Holds the rig-relative rotation between the alignment step and the retarget step. */
  private readonly scratchAligned: QuaternionTuple = [0, 0, 0, 1];

  /**
   * The `hipsY` channel, resolved ONCE here from bind-pose data (plan §4.5). Everything below is
   * in the loaded scene's WORLD space: `SourceBoneInfo.restWorldPosition` comes from
   * `skeleton.boneInverses` (or the node's `matrixWorld`) and `binding.worldCorrection` was
   * derived from those same world positions (`mapping.ts` `guessWorldCorrection`: up = Head − Hips).
   *
   * `hipsStepLocal` is null when the channel is disabled — Hips or either Foot unmapped, or a
   * degenerate leg length. Then the Hips position is never written and every `hipsY` reads 0.
   */
  private readonly upWorldVec: Vec3Tuple = [0, 1, 0];
  private readonly legLengthValue: number = 0;
  private readonly hipsObject: Object3D | null = null;
  /** Hips-parent-LOCAL displacement for `hipsY = 1`. Inverse parent scale included. */
  private readonly hipsStepLocal: Vec3Tuple | null = null;
  /** Hips local position at bind — `P⁻¹ · restWorldPosition(Hips)`, NOT `hips.position` as found. */
  private readonly hipsRestLocalPos: Vec3Tuple = [0, 0, 0];
  private lastHipsY = 0;

  constructor(
    root: Object3D,
    binding: RigBinding,
    bones: Map<CanonicalBoneId, BoundBone>,
    restInfo: ReadonlyMap<CanonicalBoneId, SourceBoneInfo>,
  ) {
    this.root = root;
    this.binding = binding;
    this.bones = bones;

    const positions: Partial<Record<CanonicalBoneId, Vec3Tuple>> = {};
    const orientations: Partial<Record<CanonicalBoneId, QuaternionTuple>> = {};
    for (const id of bones.keys()) {
      const info = restInfo.get(id);
      const p = info?.restWorldPosition;
      positions[id] = p ? [p[0], p[1], p[2]] : [0, 0, 0];
      const q = info?.restWorldQuaternion;
      orientations[id] = q ? [q[0], q[1], q[2], q[3]] : identityQuat();
    }
    this.restWorldPositions = positions;
    this.restWorldQuaternions = orientations;

    // --- the hipsY channel -------------------------------------------------------------------
    const w = normalizeQuat(binding.worldCorrection ?? identityQuat());
    const up = new Vector3(0, 1, 0).applyQuaternion(
      new Quaternion(w[0], w[1], w[2], w[3]).invert(),
    );
    if (up.lengthSq() > 1e-12) up.normalize();
    else up.set(0, 1, 0);
    this.upWorldVec = [up.x, up.y, up.z];

    const hips = positions.Hips;
    const leftFoot = positions.LeftFoot;
    const rightFoot = positions.RightFoot;
    const hipsObject = bones.get('Hips')?.object ?? null;
    if (!hips || !leftFoot || !rightFoot || !hipsObject) return;

    const height = (p: Vec3Tuple): number => p[0] * up.x + p[1] * up.y + p[2] * up.z;
    const legLength = height(hips) - Math.min(height(leftFoot), height(rightFoot));
    if (!Number.isFinite(legLength) || legLength <= 1e-6) return;

    // The Hips parent is usually a NON-bone (an Armature or a plain group) with no bind matrix of
    // its own, so the node graph is the only source for it — and it is static, which is why the
    // step vector can be baked here instead of recomputed per frame.
    root.updateMatrixWorld(true);
    const parentWorld = new Matrix4();
    if (hipsObject.parent) parentWorld.copy(hipsObject.parent.matrixWorld);

    const step = up.clone().multiplyScalar(legLength);
    step.applyMatrix3(new Matrix3().setFromMatrix4(parentWorld).invert());
    if (!Number.isFinite(step.x + step.y + step.z)) return;

    const restLocal = new Vector3(hips[0], hips[1], hips[2]).applyMatrix4(
      new Matrix4().copy(parentWorld).invert(),
    );
    if (!Number.isFinite(restLocal.x + restLocal.y + restLocal.z)) return;

    this.legLengthValue = legLength;
    this.hipsObject = hipsObject;
    this.hipsStepLocal = [step.x, step.y, step.z];
    this.hipsRestLocalPos = [restLocal.x, restLocal.y, restLocal.z];
  }

  boneObject(id: CanonicalBoneId): Object3D | undefined {
    return this.bones.get(id)?.object;
  }

  /** Canonical bones this rig actually drives, in canonical hierarchy order. */
  mappedBoneIds(): CanonicalBoneId[] {
    return CANONICAL_BONE_IDS.filter((id) => this.bones.has(id));
  }

  /**
   * Drive the skeleton from a canonical pose. Every mapped bone the pose does not mention is
   * returned to **this model's own rest** local rotation, so a clip that animates only the arms
   * never leaves the legs frozen in the previous clip's pose — and never yanks them to the
   * canonical T-pose either (the alignment applies to keyed bones only, by design).
   *
   * `hipsY` is the normalized vertical Hips offset (`RotationKeyframe.hipsY`): the Hips local
   * position becomes `hipsRestLocalPos + hipsY · hipsStep`. It is written on EVERY call when the
   * channel is enabled, so the default 0 forces the Hips back onto its bind position exactly as
   * an unkeyed bone is forced back onto `restLocal`.
   *
   * A Root-bone rotation in `pose` is NOT combined with `hipsY`: `hipsStep` is bind-static, so the
   * offset always runs along the model's bind up axis. Rotating the whole character is the game's
   * job (root belongs to the game, Hips belongs to the clip).
   */
  applyPose(pose: Pose, hipsY: number = 0): void {
    for (const bound of this.bones.values()) {
      const q_c = pose[bound.id];
      if (q_c) {
        const rel = absoluteToRelative(bound.alignment, q_c, this.scratchAligned);
        const local = canonicalToSourceLocal(bound.W_b, bound.restLocal, rel, this.scratchLocal);
        bound.object.quaternion.set(local[0], local[1], local[2], local[3]);
      } else {
        const rest = bound.restLocal;
        bound.object.quaternion.set(rest[0], rest[1], rest[2], rest[3]);
      }
    }

    const step = this.hipsStepLocal;
    if (step && this.hipsObject) {
      const base = this.hipsRestLocalPos;
      this.hipsObject.position.set(
        base[0] + hipsY * step[0],
        base[1] + hipsY * step[1],
        base[2] + hipsY * step[2],
      );
      this.lastHipsY = hipsY;
    }
  }

  /** Canonical rotation currently on a bone — `applyPose` for one bone, inverted. */
  readCanonicalLocal(id: CanonicalBoneId): QuaternionTuple {
    const bound = this.bones.get(id);
    if (!bound) return identityQuat();
    const q = bound.object.quaternion;
    this.scratchLocal[0] = q.x;
    this.scratchLocal[1] = q.y;
    this.scratchLocal[2] = q.z;
    this.scratchLocal[3] = q.w;
    const rel = sourceLocalToCanonical(bound.W_b, bound.restLocal, this.scratchLocal, this.scratchAligned);
    return relativeToAbsolute(bound.alignment, rel);
  }

  /** The whole current pose in canonical space — what keyframing reads. */
  readCanonicalPose(): Pose {
    const pose: Pose = {};
    for (const id of this.mappedBoneIds()) pose[id] = this.readCanonicalLocal(id);
    return pose;
  }

  resetToRest(): void {
    for (const bound of this.bones.values()) {
      const rest = bound.restLocal;
      bound.object.quaternion.set(rest[0], rest[1], rest[2], rest[3]);
    }
    if (this.hipsStepLocal && this.hipsObject) {
      const base = this.hipsRestLocalPos;
      this.hipsObject.position.set(base[0], base[1], base[2]);
      this.lastHipsY = 0;
    }
  }

  /** The `hipsY` last handed to `applyPose` — 0 after `resetToRest()`, at bind, and when disabled. */
  hipsY(): number {
    return this.lastHipsY;
  }

  /**
   * Bind-pose vertical distance from the Hips joint to the lower Foot joint, in the loaded
   * scene's world units. 0 when the `hipsY` channel is disabled on this rig.
   */
  legLength(): number {
    return this.legLengthValue;
  }

  /** The model's up axis as a unit vector in the loaded scene's world space. Allocates — not per frame. */
  upWorld(): Vec3Tuple {
    const u = this.upWorldVec;
    return [u[0], u[1], u[2]];
  }

  /**
   * The Hips local position that a given `hipsY` produces — what the GLB exporter bakes into its
   * one Hips position track. `null` when the channel is disabled (Hips or a Foot unmapped, or a
   * degenerate leg length), which is the exporter's signal to write no position track at all.
   */
  hipsLocalPosition(hipsY: number): Vec3Tuple | null {
    const step = this.hipsStepLocal;
    if (!step) return null;
    const base = this.hipsRestLocalPos;
    return [
      base[0] + hipsY * step[0],
      base[1] + hipsY * step[1],
      base[2] + hipsY * step[2],
    ];
  }

  /**
   * The `hipsY` the skeleton is currently sitting at, measured rather than remembered: the Hips
   * world displacement from its bind position, projected on the model's up axis and divided by
   * the leg length. This is what the importer samples per frame, so root-bone translation above
   * the Hips counts too.
   *
   * Assumes the bound root's world transform has not changed since bind — true for the importer's
   * freshly loaded file scene and for the tests. It is not defended against: a caller that moves
   * the whole character in the world owns that motion (root belongs to the game).
   */
  readHipsY(): number {
    const object = this.hipsObject;
    if (!object || this.legLengthValue <= 0) return 0;
    object.updateWorldMatrix(true, false);
    const e = object.matrixWorld.elements;
    const rest = this.restWorldPositions.Hips ?? [0, 0, 0];
    const u = this.upWorldVec;
    const d =
      (e[12] - rest[0]) * u[0] + (e[13] - rest[1]) * u[1] + (e[14] - rest[2]) * u[2];
    return d / this.legLengthValue;
  }

  /**
   * Bind-pose world positions of the mapped bones — the reference skeleton the IK solver and the
   * viewport overlay measure against. Only mapped bones appear.
   */
  restJointWorldPositions(): Partial<Record<CanonicalBoneId, Vec3Tuple>> {
    const out: Partial<Record<CanonicalBoneId, Vec3Tuple>> = {};
    for (const id of this.mappedBoneIds()) {
      const p = this.restWorldPositions[id];
      if (p) out[id] = [p[0], p[1], p[2]];
    }
    return out;
  }

  worldPosition(id: CanonicalBoneId): Vec3Tuple {
    const object = this.boneObject(id);
    if (!object) return [0, 0, 0];
    object.updateWorldMatrix(true, false);
    const e = object.matrixWorld.elements;
    return [e[12], e[13], e[14]];
  }

  /**
   * Bind-pose world orientation of a mapped bone (identity when unmapped). Paired with
   * `worldQuaternion(id)` it says how far a bone has been rotated away from the bind pose —
   * which is what the IK bend direction has to follow when the torso turns.
   */
  restWorldQuaternion(id: CanonicalBoneId): QuaternionTuple {
    const q = this.restWorldQuaternions[id];
    return q ? cloneQuat(q) : identityQuat();
  }

  worldQuaternion(id: CanonicalBoneId): QuaternionTuple {
    const object = this.boneObject(id);
    if (!object) return identityQuat();
    object.updateWorldMatrix(true, false);
    const q = object.getWorldQuaternion(worldQuatScratch);
    return [q.x, q.y, q.z, q.w];
  }

  /** Canonical rotation → the local rotation the source bone needs. Used by the GLB exporter. */
  toSourceLocal(id: CanonicalBoneId, q_c: QuaternionTuple): QuaternionTuple {
    const bound = this.bones.get(id);
    if (!bound) return identityQuat();
    const rel = absoluteToRelative(bound.alignment, q_c, this.scratchAligned);
    return canonicalToSourceLocal(bound.W_b, bound.restLocal, rel);
  }

  /**
   * The canonical pose that means "this rig's own rest".
   *
   * A canonical rotation is measured from the canonical straight T-pose (`humanoid-v2`), so
   * identity is NOT this rig's rest — on an A-pose model, feeding identity swings the arms out to
   * the T-pose. `applyPose(rig.restCanonicalPose())` instead lands every mapped bone exactly on
   * its `restLocal`, which is what `resetToRest()` and `applyPose`'s unkeyed-bone fallback do.
   *
   * That makes it the correct neutral value to blend towards: a layer fading out to weight 0
   * converges on this pose and then vanishes without the skeleton moving. Allocates a fresh Pose
   * of plain tuples — call it once and cache, never per frame.
   */
  restCanonicalPose(): Pose {
    const pose: Pose = {};
    for (const id of this.mappedBoneIds()) {
      const bound = this.bones.get(id);
      if (!bound) continue;
      // `applyPose` runs `absoluteToRelative` then `canonicalToSourceLocal(W_b, restLocal, rel)`,
      // and `rel === identity` is what yields `restLocal` unchanged. Invert just the first step.
      pose[id] = relativeToAbsolute(bound.alignment, identityQuat());
    }
    return pose;
  }

  /** Rest local rotation of a mapped bone (identity when the bone is not mapped). */
  restLocalOf(id: CanonicalBoneId): QuaternionTuple {
    const bound = this.bones.get(id);
    return bound ? cloneQuat(bound.restLocal) : identityQuat();
  }
}
